/**
 * HomePageNew — mode-aware dashboard per spec §3.1 with PullToRefresh.
 * Planning mode (no visit today): shows upcoming trips + quick actions.
 * Park mode (visit today): shows TriageCard hero + today's visit + remaining content.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useVisits } from '../hooks/useVisits';
import { useTrips } from '../hooks/useTrips';
// import { DataCard } from '../design-system/primitives/DataCard';
// import { DataCardRow } from '../design-system/primitives/DataCardRow';
// import { WaitTimeIndicator } from '../design-system/primitives/WaitTimeIndicator';
import { TriageCard } from '../design-system/primitives/TriageCard';
import { VisitCard } from '../design-system/primitives/VisitCard';
import { TripCard } from '../components/TripCard';
import { PullToRefresh } from '../components/shared/PullToRefresh';
import { EmptyState } from '../components/shared/EmptyState';
import { Ticket, Plane, Castle, MapPin, CalendarDays } from 'lucide-react';

const PRIORITY_RANK: Record<string, number> = { HIGH: 0, MEDIUM: 1, LOW: 2 };

export function HomePageNew(): React.ReactElement {
  const [mode, setMode] = React.useState<'park' | 'planning'>('park');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: visits, isLoading: visitsLoading } = useVisits();
  const { data: trips, isLoading: tripsLoading } = useTrips();

  const handleRefresh = React.useCallback(async () => {
    await Promise.all([
      queryClient.refetchQueries({ queryKey: ['visits'] }),
      queryClient.refetchQueries({ queryKey: ['trips'] }),
    ]);
  }, [queryClient]);

  const todayStr = new Date().toISOString().split('T')[0];
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const tomorrowStr = tomorrow.toISOString().split('T')[0];

  const todayVisit = visits?.find((v) => v.visitDate === todayStr);
  const tomorrowVisit = visits?.find((v) => v.visitDate === tomorrowStr);

  const upcomingTrips = trips
    ?.filter((t) => t.isActive || t.isUpcoming)
    .sort((a: any, b: any) => a.daysUntilStart - b.daysUntilStart);

  const filteredVisits = visits || [];

  // Shortest wait nearby (from today's visit's queue times)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _shortestWait = React.useMemo(() => {
    const qt = (todayVisit as any)?.queueTimes;
    if (!qt?.length) return null;
    // Only consider operating attractions
    const operating = qt.filter((q: any) => q.status !== null && q.status.toLowerCase() === 'operating' && (q.waitTimeMinutes || 0) < 60);
    if (!operating.length) return null;
    return operating.reduce((min: any, q: any) => !min || (q.waitTimeMinutes || Infinity) < (min.waitTimeMinutes || Infinity) ? q : min, null);
  }, [todayVisit]);

  // Next recommendation: highest priority active attraction with moderate wait
  const recommendation = React.useMemo(() => {
    const qt = (todayVisit as any)?.queueTimes;
    if (!qt?.length) return null;
    // Only consider operating attractions
    const active = qt.filter((q: any) => !q.completed && q.status !== null && q.status.toLowerCase() === 'operating' && (q.waitTimeMinutes || 0) < 60);
    if (!active.length) return null;
    return active.sort((a: any, b: any) => {
      const rankA = PRIORITY_RANK[a.priority] ?? 99;
      const rankB = PRIORITY_RANK[b.priority] ?? 99;
      if (rankA !== rankB) return rankA - rankB;
      return (a.waitTimeMinutes || Infinity) - (b.waitTimeMinutes || Infinity);
    })[0];
  }, [todayVisit]);

  // Auto-detect mode: if today or tomorrow has a visit → Park, else Planning
  React.useEffect(() => {
    if (todayVisit || tomorrowVisit) {
      setMode('park');
    } else if (!visitsLoading) {
      setMode('planning');
    }
  }, [todayVisit, tomorrowVisit, visitsLoading]);

  const filteredTrips = upcomingTrips;

  return (
    <PullToRefresh onRefresh={handleRefresh}>
      <div className="space-y-6">
        {/* Title */}
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
          {mode === 'park' ? 'Park Today' : 'Plan Your Trip'}
        </h1>

        {/* Mode Switcher */}
        <div className="flex gap-2">
          <button onClick={() => setMode('park')}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border transition ${mode === 'park' ? 'bg-disney-blue-500 text-white border-disney-blue-500' : 'bg-white dark:bg-surface-dark-card border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300'}`}>
            <MapPin className="w-3.5 h-3.5" />
            Park Today
          </button>
          <button onClick={() => setMode('planning')}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border transition ${mode === 'planning' ? 'bg-disney-blue-500 text-white border-disney-blue-500' : 'bg-white dark:bg-surface-dark-card border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300'}`}>
            <CalendarDays className="w-3.5 h-3.5" />
            Planning
          </button>
        </div>

        {/* Loading */}
        {visitsLoading && (
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="skeleton-queue-item">
                <div className="skeleton-queue-info">
                  <div className="skeleton-queue-header skeleton" />
                  <div className="skeleton-queue-status skeleton" />
                </div>
                <div className="skeleton-queue-actions">
                  <div className="skeleton-wait-time skeleton" />
                  <div className="skeleton-remove-button skeleton" />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* PARK MODE */}
        {mode === 'park' && !visitsLoading && (todayVisit || tomorrowVisit) && (
          <>
            {/* TriageCard hero — best attraction recommendation (today only; no live data for tomorrow) */}
            {recommendation && todayVisit && (
              <TriageCard
                mode="park"
                attractionName={recommendation.attractionName}
                waitMinutes={recommendation.waitTimeMinutes ?? null}
                attractionId={recommendation.attractionExternalId}
                visitId={todayVisit.id}
              />
            )}

            {/* Today's Visit */}
            {todayVisit && (
              <div>
                <h2 className="text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-3">Today's Visit</h2>
                <VisitCard
                  variant="primary"
                  parkName={todayVisit.parkName}
                  visitDate={todayVisit.visitDate}
                  visitPeriod={todayVisit.visitPeriod as 'MORNING' | 'MIDDAY' | 'EVENING' | undefined}
                  attractionCount={todayVisit.selectedAttractionCount ?? 0}
                  onClick={() => navigate(`/visit/${todayVisit.id}`, { state: { from: 'home' } })}
                />
              </div >
            )
            }

            {/* Tomorrow — secondary when today also has a visit */}
            {
              todayVisit && tomorrowVisit && (
                <div>
                  <h2 className="text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-3">Tomorrow</h2>
                  <VisitCard
                    parkName={tomorrowVisit.parkName}
                    visitDate={tomorrowVisit.visitDate}
                    visitPeriod={tomorrowVisit.visitPeriod as 'MORNING' | 'MIDDAY' | 'EVENING' | undefined}
                    attractionCount={tomorrowVisit.selectedAttractionCount ?? 0}
                    onClick={() => navigate(`/visit/${tomorrowVisit.id}`, { state: { from: 'home' } })}
                  />
                </div >
              )
            }

            {/* Tomorrow — primary when no visit today */}
            {
              !todayVisit && tomorrowVisit && (
                <div>
                  <h2 className="text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-3">Tomorrow's Visit</h2>
                  <VisitCard
                    variant="primary"
                    parkName={tomorrowVisit.parkName}
                    visitDate={tomorrowVisit.visitDate}
                    visitPeriod={tomorrowVisit.visitPeriod as 'MORNING' | 'MIDDAY' | 'EVENING' | undefined}
                    attractionCount={tomorrowVisit.selectedAttractionCount ?? 0}
                    onClick={() => navigate(`/visit/${tomorrowVisit.id}`, { state: { from: 'home' } })}
                  />
                </div>
              )
            }
          </>
        )}

        {/* PLANNING MODE */}
        {
          mode === 'planning' && (
            <>
              {/* TriageCard hero — next upcoming trip or empty state */}
              {upcomingTrips && upcomingTrips.length > 0 ? (
                <TriageCard
                  mode="planning"
                  tripName={upcomingTrips[0].name}
                  startDate={upcomingTrips[0].startDate}
                  endDate={upcomingTrips[0].endDate}
                  daysUntilStart={upcomingTrips[0].daysUntilStart}
                  visitCount={upcomingTrips[0].visitCount ?? 0}
                  tripId={upcomingTrips[0].id}
                />
              ) : (
                <TriageCard mode="empty" />
              )}

              {/* Quick Actions */}
              <div className="grid grid-cols-2 gap-3">
                <button onClick={() => navigate('/create')}
                  className="bg-white dark:bg-surface-dark-card rounded-xl p-4 shadow-card hover:shadow-card-hover transition-all duration-200 text-left">
                  <div className="mb-2 text-disney-blue-500 dark:text-disney-blue-400">
                    <Ticket className="w-6 h-6" />
                  </div>
                  <h3 className="font-semibold text-gray-900 dark:text-gray-100">Plan Visit</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Create a new park visit</p>
                </button>
                <button onClick={() => navigate('/my-trips')}
                  className="bg-white dark:bg-surface-dark-card rounded-xl p-4 shadow-card hover:shadow-card-hover transition-all duration-200 text-left">
                  <div className="mb-2 text-disney-purple-500 dark:text-disney-purple-400">
                    <Plane className="w-6 h-6" />
                  </div>
                  <h3 className="font-semibold text-gray-900 dark:text-gray-100">My Trips</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{trips?.length || 0} trips planned</p>
                </button>
              </div>

              {/* Upcoming Trips — skip the first trip if it's already shown in the TriageCard hero */}
              {!tripsLoading && filteredTrips && filteredTrips.filter((t) => t.id !== upcomingTrips?.[0]?.id).length > 0 && (
                <div>
                  <h2 className="text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-3">Upcoming Trips</h2>
                  <div className="space-y-3">
                    {filteredTrips.filter((t) => t.id !== upcomingTrips?.[0]?.id).map((trip) => (
                      <TripCard
                        key={trip.id}
                        trip={trip}
                        isNext
                        onSelect={() => navigate(`/trip/${trip.id}`, { state: { from: 'home' } })}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Recent Visits */}
              {filteredVisits.length > 0 && (
                <div>
                  <h2 className="text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-3">Recent Visits</h2>
                  <div className="space-y-3">
                    {filteredVisits.slice(0, 5).map((visit) => (
                      <VisitCard
                        key={visit.id}
                        parkName={visit.parkName}
                        visitDate={visit.visitDate}
                        visitPeriod={visit.visitPeriod as 'MORNING' | 'MIDDAY' | 'EVENING' | undefined}
                        attractionCount={visit.selectedAttractionCount ?? 0}
                        onClick={() => navigate(`/visit/${visit.id}`)}
                      />
                    ))}
                  </div>
                </div>
              )}
            </>
          )
        }

        {/* Empty State */}
        {
          !visitsLoading && !todayVisit && mode === 'park' && (
            <EmptyState icon={Castle} iconColor="text-disney-gold-400"
              title="No visit today" subtitle="Switch to Planning mode to organize your trip." />
          )
        }
      </div >
    </PullToRefresh >
  );
}
